import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, extractError } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';

/**
 * Ein Formular für Zugang und Mitarbeiter – bewusst an beiden Stellen dasselbe.
 *
 * Aufruf aus der Mitarbeiterliste:      modus="mitarbeiter"
 *   Der Mitarbeiter-Teil ist fest eingeschaltet. Ein Mitarbeiter ohne Zugang
 *   könnte sich nicht anmelden und seine Einsätze nie sehen.
 *
 * Aufruf aus Einstellungen → Benutzer:  modus="benutzer"
 *   Der Mitarbeiter-Teil hängt an der Checkbox. Wer nur plant (Büro,
 *   Administration), braucht keinen Eintrag in der Plantafel.
 *
 * Anmelden kann man sich später wahlweise mit der E-Mail-Adresse oder mit dem
 * Benutzernamen – gedacht für Monteure ohne eigene Firmenadresse.
 */
export default function PersonDialog({ open, onOpenChange, modus = 'benutzer', onSaved }) {
  const istMitarbeiterModus = modus === 'mitarbeiter';
  const leer = {
    first_name: '', last_name: '', email: '', username: '', password: '',
    role: istMitarbeiterModus ? 'viewer' : 'viewer',
    cost_center_id: null, active: true,
    create_employee: istMitarbeiterModus,
    employee_type: 'Mitarbeiter',
    employee_cost_center_id: '',
    employee_start_date: new Date().toISOString().slice(0, 10),
    employee_end_date: '',
    employee_assignment_context: 'construction',
    employee_notes: '',
  };
  const [form, setForm] = useState(leer);
  const [costCenters, setCostCenters] = useState([]);
  const [saving, setSaving] = useState(false);

  const setzen = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(leer);
    api.get('/cost-centers').then(r => {
      setCostCenters(r.data);
      if (r.data.length) setForm(f => ({ ...f, employee_cost_center_id: f.employee_cost_center_id || r.data[0].id }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const brauchtEnddatum = form.create_employee
    && ['Leiharbeiter', 'Praktikant'].includes(form.employee_type);

  const speichern = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('Vor- und Nachname sind Pflicht.'); return;
    }
    // Entweder-oder: Ohne Kennung gibt es keinen Weg in die App. Welche von
    // beiden es ist, bleibt der Firma überlassen – Monteure haben oft keine
    // Firmenadresse, und eine erfundene wäre schlechter als keine.
    if (!form.email.trim() && !form.username.trim()) {
      toast.error('Bitte eine E-Mail-Adresse oder einen Benutzernamen angeben.'); return;
    }
    if (form.email.trim() && !form.email.includes('@')) {
      toast.error('Die E-Mail-Adresse sieht nicht richtig aus.'); return;
    }
    if (form.password.length < 10) { toast.error('Das Passwort muss mindestens 10 Zeichen haben.'); return; }
    if (brauchtEnddatum && !form.employee_end_date) {
      toast.error(`Für ${form.employee_type} ist ein Enddatum Pflicht.`); return;
    }
    setSaving(true);
    try {
      const rumpf = {
        ...form,
        username: form.username.trim() || null,
        employee_end_date: form.employee_end_date || null,
        employee_notes: form.employee_notes || null,
      };
      // Beide Wege landen im selben Code auf dem Server.
      await api.post(istMitarbeiterModus ? '/employees' : '/users', rumpf);
      toast.success(istMitarbeiterModus
        ? 'Mitarbeiter samt Zugang angelegt.'
        : (form.create_employee ? 'Benutzer und Mitarbeiter angelegt.' : 'Benutzer angelegt.'));
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(extractError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {istMitarbeiterModus ? 'Mitarbeiter anlegen' : 'Benutzer anlegen'}
          </DialogTitle>
          <DialogDescription>
            {istMitarbeiterModus
              ? 'Jeder Mitarbeiter bekommt einen Zugang – ohne kann er sich nicht anmelden und seine Einsätze nicht sehen.'
              : 'Zugang zur App. Wer auch in der Plantafel eingeplant werden soll, bekommt zusätzlich einen Mitarbeiter-Datensatz.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Zugang</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vorname *</Label>
              <Input value={form.first_name} onChange={e => setzen('first_name', e.target.value)} data-testid="person-firstname" />
            </div>
            <div>
              <Label>Nachname *</Label>
              <Input value={form.last_name} onChange={e => setzen('last_name', e.target.value)} data-testid="person-lastname" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={form.email} onChange={e => setzen('email', e.target.value)} data-testid="person-email" />
            </div>
            <div>
              <Label>Benutzername</Label>
              <Input value={form.username} onChange={e => setzen('username', e.target.value)}
                     placeholder="z. B. b.kaufmann" data-testid="person-username" />
            </div>
            {/* Ein Hinweis für beide Felder statt zwei getrennte – sonst liest
                man zweimal "optional" und hält beides für verzichtbar. */}
            <div className="col-span-2 -mt-2">
              <p className="text-xs text-slate-500">
                Eines von beiden genügt. Angemeldet wird sich mit dem, was ausgefüllt ist;
                sind beide gefüllt, geht es wahlweise.
              </p>
            </div>
            <div>
              <Label>Startpasswort *</Label>
              <Input type="password" value={form.password} onChange={e => setzen('password', e.target.value)} data-testid="person-password" />
              <p className="text-xs text-slate-500 mt-1">Mind. 10 Zeichen, 1 Zahl, 1 Buchstabe. Muss beim ersten Anmelden geändert werden.</p>
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={form.role} onValueChange={v => setzen('role', v)}>
                <SelectTrigger data-testid="person-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="projektleiter">Projektleiter</SelectItem>
                  <SelectItem value="viewer">Betrachter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!istMitarbeiterModus && (
            <label className="flex items-center gap-2 text-sm font-semibold rounded-md border border-slate-200 bg-white px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-slate-900"
                checked={form.create_employee}
                onChange={e => setzen('create_employee', e.target.checked)}
                data-testid="person-create-employee"
              />
              Zusätzlich einen Mitarbeiter für diese Person anlegen (für die Plantafel)
            </label>
          )}

          {form.create_employee && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-2">Mitarbeiter</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Typ</Label>
                  <Select value={form.employee_type} onValueChange={v => setzen('employee_type', v)}>
                    <SelectTrigger data-testid="person-emp-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Mitarbeiter', 'Azubi', 'Leiharbeiter', 'Praktikant'].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kostenstelle</Label>
                  <Select value={form.employee_cost_center_id || ''} onValueChange={v => setzen('employee_cost_center_id', v)}>
                    <SelectTrigger data-testid="person-emp-kst"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                    <SelectContent>
                      {costCenters.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Startdatum</Label>
                  <Input type="date" value={form.employee_start_date}
                         onChange={e => setzen('employee_start_date', e.target.value)} data-testid="person-emp-start" />
                </div>
                <div>
                  <Label>Enddatum {brauchtEnddatum && '*'}</Label>
                  <Input type="date" value={form.employee_end_date}
                         onChange={e => setzen('employee_end_date', e.target.value)} data-testid="person-emp-end" />
                  {brauchtEnddatum && (
                    <p className="text-xs text-amber-700 mt-1">Bei {form.employee_type} ist ein Enddatum Pflicht.</p>
                  )}
                </div>
                <div>
                  <Label>Zuordnung</Label>
                  <Select value={form.employee_assignment_context}
                          onValueChange={v => setzen('employee_assignment_context', v)}>
                    <SelectTrigger data-testid="person-emp-context"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="construction">Baustelle</SelectItem>
                      <SelectItem value="device">Geräte</SelectItem>
                      <SelectItem value="office">Büro / Leitung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notiz</Label>
                  <Input value={form.employee_notes} onChange={e => setzen('employee_notes', e.target.value)}
                         data-testid="person-emp-notes" />
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={speichern} disabled={saving} data-testid="person-save">
            {saving ? 'Speichere…' : 'Anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
